import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getUser, creditUser, toggleBeta,
  type UserDetail, type Purchase, type Generation, type CreditLogEntry,
  type UserNote, type RewardEvent, type ReferralInfo,
} from "@/api/adminApi";
import { CreditDialog } from "@/components/CreditDialog";
import { NoteSection } from "@/components/NoteSection";

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(d: string): string {
  return new Date(d).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function formatCurrency(cents: number): string {
  return "$" + (cents / 100).toFixed(2);
}

// Pack + reward labels: source-of-truth lives in edge function _shared.ts.
// UI adds credit-count suffixes for the pack label.
const PACK_LABELS: Record<string, string> = {
  starter: "Starter (100)",
  standard: "Standard (400)",
  pro: "Pro (1,200)",
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  social_share: "Social Share",
  reviewer_link: "Reviewer Link",
  referral_given: "Referral Given",
  referral_received: "Referral Received",
};

function statusBadge(status: string) {
  const cls = status === "completed" ? "badge-success" : status === "failed" ? "badge-danger" : "badge-muted";
  return <span className={`badge ${cls}`}>{status}</span>;
}

function eventTypeBadge(type: string) {
  const cls = type.includes("referral") ? "badge-gold" : type === "social_share" ? "badge-success" : "badge-muted";
  return <span className={`badge ${cls}`}>{EVENT_TYPE_LABELS[type] ?? type}</span>;
}

type GenFilter = "all" | "completed" | "failed";

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [creditLog, setCreditLog] = useState<CreditLogEntry[]>([]);
  const [notes, setNotes] = useState<UserNote[]>([]);
  const [rewardEvents, setRewardEvents] = useState<RewardEvent[]>([]);
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCredit, setShowCredit] = useState(false);
  const [genFilter, setGenFilter] = useState<GenFilter>("all");
  const [copied, setCopied] = useState<string | null>(null);

  const loadUser = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await getUser(userId);
      setUser(res.user);
      setPurchases(res.purchases);
      setGenerations(res.generations);
      setCreditLog(res.credit_log);
      setNotes(res.notes);
      setRewardEvents(res.reward_events);
      setReferralInfo(res.referral_info);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const handleCredit = async (amount: number, reason: string) => {
    if (!userId) return;
    await creditUser(userId, amount, reason);
    setShowCredit(false);
    await loadUser();
  };

  const handleToggleBeta = async () => {
    if (!userId) return;
    await toggleBeta(userId);
    await loadUser();
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  if (loading) return <div className="loading">Loading patron file...</div>;
  if (!user) return <div className="empty-state">Patron not found</div>;

  const initials = (user.display_name || user.email).slice(0, 2).toUpperCase();

  const filteredGens = genFilter === "all"
    ? generations
    : generations.filter((g) => g.status === genFilter);

  return (
    <div>
      <button className="back-btn" onClick={() => navigate("/users")}>
        &larr; Back to Users
      </button>

      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">{initials}</div>
        <div className="profile-info">
          <h2>{user.display_name || user.email}</h2>
          <p>{user.email} &middot; Joined {formatDate(user.created_at)} &middot; Last seen {formatDate(user.last_sign_in_at)}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions" style={{ marginBottom: 16 }}>
        <button className="btn-small" onClick={() => copyToClipboard(user.id, "ID")}>
          {copied === "ID" ? "Copied!" : "Copy ID"}
        </button>
        <button className="btn-small" onClick={() => copyToClipboard(user.email, "Email")}>
          {copied === "Email" ? "Copied!" : "Copy Email"}
        </button>
        <button
          className={`btn-small ${user.is_beta ? "active" : ""}`}
          onClick={handleToggleBeta}
        >
          {user.is_beta ? "Remove Beta" : "Grant Beta"}
        </button>
        {referralInfo?.code && (
          <button className="btn-small" onClick={() => copyToClipboard(referralInfo.code!, "Code")}>
            {copied === "Code" ? "Copied!" : `Referral: ${referralInfo.code}`}
          </button>
        )}
      </div>

      {/* Balance + Credit */}
      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        <div className="card stat-card" style={{ flex: 1 }}>
          <div className="stat-label">Available Credits</div>
          <div className="stat-value">{user.credits.toLocaleString()}</div>
        </div>
        <div className="card stat-card" style={{ flex: 1 }}>
          <div className="stat-label">Credits Used</div>
          <div className="stat-value">{user.used.toLocaleString()}</div>
        </div>
        <div className="card stat-card" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <div className="stat-label">Status</div>
            <div className="stat-value" style={{ fontSize: 20 }}>
              {user.is_beta ? <span className="badge badge-gold" style={{ fontSize: 14, padding: "4px 12px" }}>Beta</span> : "Standard"}
            </div>
          </div>
          <button className="btn-gold" style={{ marginTop: 12 }} onClick={() => setShowCredit(true)}>
            Add Credits
          </button>
        </div>
      </div>

      {/* Referral Info */}
      {referralInfo && (referralInfo.referred_by_user || referralInfo.referrals.length > 0) && (
        <>
          <h3 className="section-title">Referrals</h3>
          <div className="card" style={{ padding: 16 }}>
            {referralInfo.referred_by_user && (
              <p style={{ fontSize: 13, marginBottom: referralInfo.referrals.length > 0 ? 12 : 0 }}>
                Referred by:{" "}
                <span
                  style={{ color: "var(--gold)", cursor: "pointer" }}
                  onClick={() => navigate(`/users/${referralInfo.referred_by_user!.id}`)}
                >
                  {referralInfo.referred_by_user.display_name || referralInfo.referred_by_user.email}
                </span>
              </p>
            )}
            {referralInfo.referrals.length > 0 && (
              <>
                <p style={{ fontSize: 11, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                  Referred Users ({referralInfo.referrals.length})
                </p>
                {referralInfo.referrals.map((r) => (
                  <div
                    key={r.id}
                    style={{ fontSize: 13, padding: "4px 0", cursor: "pointer", color: "var(--gold)" }}
                    onClick={() => navigate(`/users/${r.id}`)}
                  >
                    {r.display_name || r.email} — {formatDate(r.created_at)}
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}

      {/* Support Notes */}
      {userId && (
        <NoteSection userId={userId} notes={notes} onNoteAdded={loadUser} />
      )}

      {/* Reward Events */}
      {rewardEvents.length > 0 && (
        <>
          <h3 className="section-title">Reward Events</h3>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr><th>Type</th><th>Credits</th><th>Date</th></tr>
              </thead>
              <tbody>
                {rewardEvents.map((r) => (
                  <tr key={r.id}>
                    <td>{eventTypeBadge(r.event_type)}</td>
                    <td style={{ color: "var(--success)" }}>+{r.credits_awarded}</td>
                    <td>{formatDateTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Credit Log */}
      {creditLog.length > 0 && (
        <>
          <h3 className="section-title">Credit Adjustments</h3>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr><th>Amount</th><th>Reason</th><th>Date</th></tr>
              </thead>
              <tbody>
                {creditLog.map((c) => (
                  <tr key={c.id}>
                    <td style={{ color: c.amount < 0 ? "var(--danger, #e04040)" : "var(--success)" }}>{c.amount > 0 ? "+" : ""}{c.amount.toLocaleString()}</td>
                    <td>{c.reason || "—"}</td>
                    <td>{formatDateTime(c.granted_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Purchase History */}
      <h3 className="section-title">Purchase History</h3>
      {purchases.length === 0 ? (
        <div className="empty-state">No ticket stubs on file</div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr><th>Pack</th><th>Credits</th><th>Amount</th><th>Source</th><th>Date</th><th>Status</th></tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id}>
                  <td>{PACK_LABELS[p.pack_id] ?? p.pack_id}</td>
                  <td>{p.tokens_purchased.toLocaleString()}</td>
                  <td>{formatCurrency(p.amount_cents)}</td>
                  <td>{p.source ?? "—"}</td>
                  <td>{formatDateTime(p.created_at)}</td>
                  <td>{statusBadge(p.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Generations */}
      <h3 className="section-title">
        Generations
        <span style={{ float: "right", display: "flex", gap: 4 }}>
          {(["all", "completed", "failed"] as const).map((f) => (
            <button
              key={f}
              className={`range-btn ${genFilter === f ? "active" : ""}`}
              style={{ padding: "3px 10px", fontSize: 10 }}
              onClick={() => setGenFilter(f)}
            >
              {f === "all" ? `All (${generations.length})` : f}
            </button>
          ))}
        </span>
      </h3>
      {filteredGens.length === 0 ? (
        <div className="empty-state">No features produced</div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Model</th>
                <th>Cost</th>
                <th>Status</th>
                <th style={{ maxWidth: 200 }}>Prompt</th>
                {genFilter === "failed" && <th>Error</th>}
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredGens.map((g) => (
                <tr key={g.id}>
                  <td style={{ textTransform: "capitalize" }}>{g.type}</td>
                  <td>{g.provider}</td>
                  <td>{g.token_cost}</td>
                  <td>{statusBadge(g.status)}</td>
                  <td
                    style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={g.prompt}
                  >
                    {g.prompt || "—"}
                  </td>
                  {genFilter === "failed" && (
                    <td style={{ color: "var(--danger)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={g.error ?? ""}>
                      {g.error || "—"}
                    </td>
                  )}
                  <td>{formatDateTime(g.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCredit && (
        <CreditDialog
          userName={user.display_name || user.email}
          onConfirm={handleCredit}
          onCancel={() => setShowCredit(false)}
        />
      )}
    </div>
  );
}
